import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English ships with the app (it is also the fallback). Every other language
// is its own chunk, fetched when selected: all twelve used to be in the initial
// download (~1.3 MB of JSON).
import en from './locales/en.json';

const loaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  es: () => import('./locales/es.json'),
  hi: () => import('./locales/hi.json'),
  bn: () => import('./locales/bn.json'),
  ur: () => import('./locales/ur.json'),
  vi: () => import('./locales/vi.json'),
  ar: () => import('./locales/ar.json'),
  fr: () => import('./locales/fr.json'),
  zh: () => import('./locales/zh.json'),
  'nwy-en': () => import('./locales/nwy-en.json'),
  'nwy-ur': () => import('./locales/nwy-ur.json'),
  kn: () => import('./locales/kn.json'),
};

const resources = {
  en: { translation: en },
};

/** Loads a language's strings if they are not in memory yet (en-IN falls back to en, hi-IN to hi). */
export async function ensureLanguage(lng: string | undefined): Promise<void> {
  if (!lng) return;
  const code = loaders[lng] ? lng : lng.split('-')[0];
  if (!loaders[code] || i18n.hasResourceBundle(code, 'translation')) return;
  try {
    const strings = await loaders[code]();
    i18n.addResourceBundle(code, 'translation', strings.default, true, true);
  } catch (error) {
    // Offline or a stale deploy: the UI stays in English (the fallback) rather than failing.
    console.warn(`Could not load the '${code}' translations`, error);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    // Log missing translation keys during development without overriding fallbackLng
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lng, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`🌍 Missing translation in '${lng}': ${ns}:${key}`);
      }
    },
    
    interpolation: {
      escapeValue: false, // React already does escaping
    },

    // Re-render when a lazily loaded language arrives (ensureLanguage).
    react: {
      bindI18nStore: 'added',
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  });

// A language chosen later (LanguageContext, Settings) is fetched on the switch.
i18n.on('languageChanged', (lng) => void ensureLanguage(lng));

/** Resolves once the saved/detected language is loaded; main.tsx waits for it before rendering. */
export const i18nReady: Promise<void> = ensureLanguage(i18n.language);

// Development helper to find missing translations
if (process.env.NODE_ENV === 'development') {
  (window as any).findMissingTranslations = () => {
    const missingKeys = new Set<string>();
    const currentLang = i18n.language;
    
    // Override the missing key handler temporarily to collect keys
    const originalHandler = i18n.options.missingKeyHandler;
    i18n.options.missingKeyHandler = (_lng, _ns, key) => {
      missingKeys.add(key);
      return key;
    };
    
    // Force re-render to trigger missing key detection
    i18n.emit('languageChanged');
    
    // Restore original handler
    i18n.options.missingKeyHandler = originalHandler;
    
    console.log(`🌍 Missing translations for ${currentLang}:`, Array.from(missingKeys));
    return Array.from(missingKeys);
  };
}

export default i18n;
